import { fallbackLookUpTable } from './focus-fallback';
import type { AvailableFocusElementsPerLayer, FocusItem, FocusLayer } from './focus-types';

/**
 *  The focus manager re-sets the focus after it is lost due to a layer change in the player ui.
 *
 *  This is needed because at the moment we have different button instances for the same button:
 *
 *  e.g. in prime controls
 *
 *       - start button (in start layer)
 *       - play/pause button (in content layer)
 *       - replay button (in replay layer)
 *
 *       are different instances, so focus is lost whenever they get replaced.
 *
 *
 *  Brief overview:
 *
 *  0) Preconditions - focus action
 *
 *    For the focus re-set to work we need to introduce each element that can be focused.
 *
 *    This is achieved using a Svelte use-action, which is currently applied to
 *    <Button> in the IconButton component,
 *    <Button> in the MenuItemRadio component,
 *    and <div class="knob"> in the Slider component.
 *
 *    Furthermore, the properties 'focusLayer' and 'focusName' have to be set and passed through to the focus action.
 *
 *  1) Initialize Focus Manager
 *
 *    The focus manager attaches two event listeners to each focusable element.
 *
 *    - "focus" to save the element focused by the user.
 *      We only want to save focus set by the user (see below: Particularities 1).
 *    - "blur" to check if the focus left the player, as we do not want to re-set focus in this case
 *      and to check if we lost the focus.
 *
 *  2) Inform the focus manager about layer changes
 *
 *    Whenever the Player component changes, we want to check if we lost the focus and need to re-set the focus.
 *    To achieve this, we utilize the afterUpdate lifecycle hook.
 *
 *  Particularities:
 *
 *  1) only save if focused by user
 *  Calling the focus() function on an element will emit a "focus" event.
 *  We do not want to save this on re-setting the focus, so we flag the next focus event to NOT save it:
 *
 *  e.g.:
 *  - focus is on play
 *  - user(tab) -> focus switches to audioTrack
 *  - ad starts -> setActiveLayer('advertising') -> try to find element audiotrack in advertising layer
 *                                               -> not found so try to find play (fallback)
 *                                               -> found, so re-set focus to play
 *                                               -> we do NOT save play, as it was not focused by the user
 *  - ad ends -> setActiveLayer('content') -> try to find element audiotrack in content layer
 *                                         -> found, so re-set focus to audiotrack
 *
 *  In contrast: If the user interacts during the ad, we would save the focus:
 *
 *  - focus is on play
 *  - user(tab) -> focus switches to audioTrack
 *  - ad starts -> setActiveLayer('advertising') -> try to find element audiotrack in advertising layer
 *                                               -> not found so try to find play (fallback)
 *                                               -> found, so re-set focus to play
 *                                               -> we do NOT save play, as it was not focused by the user
 *  - user(tab) -> focus switches to volume      -> save volume as focused element
 *
 *  - ad ends -> setActiveLayer('content') -> try to find element volume in content layer
 *                                         -> found, so re-set focus to volume
 *
 */
export class FocusManager {
  private currentFocusElementName: FocusItem = 'playPause';
  private focusLayer: FocusLayer | undefined = undefined;
  private readonly availableFocusElements: AvailableFocusElementsPerLayer = {
    advertising: {
      audioTrack: undefined,
      audioTrackOption: undefined,
      close: undefined,
      fullscreen: undefined,
      playNextMedium: undefined,
      playPause: undefined,
      playbackSpeed: undefined,
      playbackSpeedOption: undefined,
      playPausePrime: undefined,
      replay: undefined,
      replayPrime: undefined,
      seekBackward: undefined,
      seekBackwardPrime: undefined,
      seekbarSlider: undefined,
      seekForward: undefined,
      seekForwardPrime: undefined,
      share: undefined,
      start: undefined,
      startPrime: undefined,
      textTrack: undefined,
      textTrackOption: undefined,
      volume: undefined,
      volumeSlider: undefined,
    },
    content: {
      audioTrack: undefined,
      audioTrackOption: undefined,
      close: undefined,
      fullscreen: undefined,
      playNextMedium: undefined,
      playPause: undefined,
      playbackSpeed: undefined,
      playbackSpeedOption: undefined,
      playPausePrime: undefined,
      replay: undefined,
      replayPrime: undefined,
      seekBackward: undefined,
      seekBackwardPrime: undefined,
      seekbarSlider: undefined,
      seekForward: undefined,
      seekForwardPrime: undefined,
      share: undefined,
      start: undefined,
      startPrime: undefined,
      textTrack: undefined,
      textTrackOption: undefined,
      volume: undefined,
      volumeSlider: undefined,
    },
    loading: {
      audioTrack: undefined,
      audioTrackOption: undefined,
      close: undefined,
      fullscreen: undefined,
      playNextMedium: undefined,
      playPause: undefined,
      playbackSpeed: undefined,
      playbackSpeedOption: undefined,
      playPausePrime: undefined,
      replay: undefined,
      replayPrime: undefined,
      seekBackward: undefined,
      seekBackwardPrime: undefined,
      seekbarSlider: undefined,
      seekForward: undefined,
      seekForwardPrime: undefined,
      share: undefined,
      start: undefined,
      startPrime: undefined,
      textTrack: undefined,
      textTrackOption: undefined,
      volume: undefined,
      volumeSlider: undefined,
    },
    nextMediumPreview: {
      audioTrack: undefined,
      audioTrackOption: undefined,
      close: undefined,
      fullscreen: undefined,
      playNextMedium: undefined,
      playPause: undefined,
      playbackSpeed: undefined,
      playbackSpeedOption: undefined,
      playPausePrime: undefined,
      replay: undefined,
      replayPrime: undefined,
      seekBackward: undefined,
      seekBackwardPrime: undefined,
      seekbarSlider: undefined,
      seekForward: undefined,
      seekForwardPrime: undefined,
      share: undefined,
      start: undefined,
      startPrime: undefined,
      textTrack: undefined,
      textTrackOption: undefined,
      volume: undefined,
      volumeSlider: undefined,
    },
    start: {
      audioTrack: undefined,
      audioTrackOption: undefined,
      close: undefined,
      fullscreen: undefined,
      playNextMedium: undefined,
      playPause: undefined,
      playbackSpeed: undefined,
      playbackSpeedOption: undefined,
      playPausePrime: undefined,
      replay: undefined,
      replayPrime: undefined,
      seekBackward: undefined,
      seekBackwardPrime: undefined,
      seekbarSlider: undefined,
      seekForward: undefined,
      seekForwardPrime: undefined,
      share: undefined,
      start: undefined,
      startPrime: undefined,
      textTrack: undefined,
      textTrackOption: undefined,
      volume: undefined,
      volumeSlider: undefined,
    },
    replay: {
      audioTrack: undefined,
      audioTrackOption: undefined,
      close: undefined,
      fullscreen: undefined,
      playNextMedium: undefined,
      playPause: undefined,
      playbackSpeed: undefined,
      playbackSpeedOption: undefined,
      playPausePrime: undefined,
      replay: undefined,
      replayPrime: undefined,
      seekBackward: undefined,
      seekBackwardPrime: undefined,
      seekbarSlider: undefined,
      seekForward: undefined,
      seekForwardPrime: undefined,
      share: undefined,
      start: undefined,
      startPrime: undefined,
      textTrack: undefined,
      textTrackOption: undefined,
      volume: undefined,
      volumeSlider: undefined,
    },
  };

  private focusOnPlayer = false;
  private focusLost = false;
  private dontSaveNextFocus = false;

  saveFocusedElementName(focusedElementName: FocusItem) {
    if (this.dontSaveNextFocus === false) {
      this.focusLost = false;
      this.currentFocusElementName = focusedElementName;
    }

    this.focusOnPlayer = true;
    this.dontSaveNextFocus = false;
  }

  checkIfFocusLeftPlayer(blurEvent: FocusEvent) {
    if (blurEvent.relatedTarget !== null) {
      this.focusOnPlayer = false;
    }
  }

  addFocusElementAndSetActiveLayer(
    focusElement: HTMLElement | undefined,
    focusElementName: FocusItem,
    layer: FocusLayer,
  ) {
    this.availableFocusElements[layer][focusElementName] = focusElement;
    this.focusLayer = layer;
  }

  removeFocusElementAndCheckFocusLost(focusElementName: FocusItem, layer: FocusLayer) {
    this.availableFocusElements[layer][focusElementName] = undefined;

    if (focusElementName === this.currentFocusElementName) {
      this.focusLost = true;
    }
  }

  /**
   * This function tries to (re)set the focus after it was lost e.g. due to layer change.
   *
   * It performs the following steps:
   *  - Search the current layer for a focusable element that has the saved focus-name property.
   *  - If no element with the saved name exists, search again using the defined fallback focus-name.
   *  - Call focus() on the found element.
   *
   * If neither the saved nor the fallback name was found nothing happens.
   *
   * Note: This function will be potentially triggered after checkReSetFocusNeeded() function call.
   *
   */
  private updateFocus() {
    // We only want to save focus set by the user.
    //
    // To prevent saving a focus triggered by this function, we set a flag that suppresses it.
    this.dontSaveNextFocus = true;

    let focusElementName: FocusItem = this.currentFocusElementName;

    // Automatic focus on the volume slider is currently NOT functioning.
    // This issue appears to be caused by a CSS transition.
    //
    // For now we just fall back to the volume button.
    if (focusElementName === 'volumeSlider') {
      focusElementName = 'volume';
    }

    // Automatic focus on Options (in Menus) does not work as well, as the menus won't be open.
    // We fall back to the button.
    if (focusElementName === 'playbackSpeedOption') {
      focusElementName = 'playbackSpeed';
    }
    if (focusElementName === 'audioTrackOption') {
      focusElementName = 'audioTrack';
    }
    if (focusElementName === 'textTrackOption') {
      focusElementName = 'textTrack';
    }

    let focusElement: HTMLElement | undefined = this.getAvailableElement(this.focusLayer, focusElementName);

    if (focusElement === undefined && this.focusLayer !== undefined) {
      const fallback: FocusItem | '' = fallbackLookUpTable[this.focusLayer][focusElementName];

      if (fallback !== '') {
        focusElementName = fallback;
        focusElement = this.getAvailableElement(this.focusLayer, focusElementName);
      }
    }

    if (focusElement instanceof HTMLElement) {
      focusElement.focus();
    } else {
      // if no element is focused we can remove the reset flag, that prevents saving the focus.
      this.dontSaveNextFocus = false;
    }
  }

  private getAvailableElement(layer: FocusLayer | undefined, name: FocusItem) {
    if (layer === undefined) {
      return undefined;
    }
    return this.availableFocusElements[layer][name];
  }

  // This function is called after the player component has been updated.
  // To achieve this, we utilize the afterUpdate lifecycle hook in the Player.svelte component.
  updateFocusIfNeeded() {
    if (!this.focusOnPlayer) {
      return;
    }

    if (this.focusLost) {
      this.updateFocus();
    }
  }
}
